import { useParams } from 'react-router-dom';
import { FirestoreError } from 'firebase/firestore';
import useVersionCollection from '@actions/services/firestore/browser/workflow/version/useCollection';
import useWorkflowDoc from '@actions/services/firestore/browser/workflow/use';
import { WorkflowVersion } from '../../pages/builder/Canvas/WorkflowToolbar/VersionDropdown/types';

interface UseWorkflowVersionsResult {
    versions: WorkflowVersion[];
    isLoadingVersions: boolean;
    error: FirestoreError | null;
}

/**
 * Hook to load all versions for the current workflow from Firestore
 * Automatically gets workflowId from URL parameters and determines version tags
 * Returns data transformed for UI consumption with tags: published, draft, archived
 *
 * @returns Object containing transformed versions array, loading state, and error state
 *
 * @example
 * ```tsx
 * const { versions, isLoadingVersions, error } = useWorkflowVersions();
 *
 * if (isLoadingVersions) return <div>Loading versions...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return (
 *   <div>
 *     {versions.map(version => (
 *       <div key={version.id}>
 *         {version.id} - {version.tags.join(', ')}
 *       </div>
 *     ))}
 *   </div>
 * );
 * ```
 */
const useWorkflowVersions = (): UseWorkflowVersionsResult => {
    const { workflowId } = useParams();

    const {
        data: workflow,
        isLoading: isLoadingWorkflow,
        error: workflowError,
    } = useWorkflowDoc(workflowId!, {
        disabled: !workflowId,
    });

    const {
        data: firestoreVersions,
        isLoading: isLoadingVersions,
        error: versionsError,
    } = useVersionCollection(workflowId!, {
        disabled: !workflowId,
    });

    // Transform Firestore versions to UI format with tags
    const versions: WorkflowVersion[] = firestoreVersions.map(version => {
        const tags: ('published' | 'draft' | 'archived')[] = [];

        // Check if this version is published
        if (workflow?.publishedVersionId === version.id) {
            tags.push('published');
        }
        // Check if this version is draft
        else if (workflow?.draftVersionId === version.id) {
            tags.push('draft');
        }
        // If neither published nor draft, it's archived
        else {
            tags.push('archived');
        }

        return {
            id: version.id,
            createdAt: version.createdAt.toISOString(),
            updatedAt: version.updatedAt.toISOString(),
            tags,
        };
    });

    return {
        versions,
        isLoadingVersions: isLoadingWorkflow || isLoadingVersions,
        error: workflowError || versionsError,
    };
};

export default useWorkflowVersions;
